/**
 * SimpleGameWorld - Minimal 3D game container with physics
 * 
 * Simplified version that initializes Rapier properly before rendering.
 * Updated: Force rebuild after babel plugin fix for Three.js elements
 */

import { Suspense, useState, useCallback, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Physics, RigidBody, CuboidCollider, CapsuleCollider } from '@react-three/rapier';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

// Simple player character with basic physics
function Player() {
  const rigidBodyRef = useRef();
  const { camera } = useThree();
  const [position, setPosition] = useState({ x: 0, y: 1, z: 10 });
  const keys = useRef({ w: false, a: false, s: false, d: false, shift: false });
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'KeyW') keys.current.w = true;
      if (e.code === 'KeyA') keys.current.a = true;
      if (e.code === 'KeyS') keys.current.s = true;
      if (e.code === 'KeyD') keys.current.d = true;
      if (e.code === 'ShiftLeft') keys.current.shift = true;
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'KeyW') keys.current.w = false;
      if (e.code === 'KeyA') keys.current.a = false;
      if (e.code === 'KeyS') keys.current.s = false;
      if (e.code === 'KeyD') keys.current.d = false;
      if (e.code === 'ShiftLeft') keys.current.shift = false;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  useFrame((state, delta) => {
    if (!rigidBodyRef.current) return;
    
    const speed = keys.current.shift ? 10 : 5;
    const rb = rigidBodyRef.current;
    const pos = rb.translation();
    
    // Calculate movement
    let moveX = 0, moveZ = 0;
    if (keys.current.w) moveZ -= speed;
    if (keys.current.s) moveZ += speed;
    if (keys.current.a) moveX -= speed;
    if (keys.current.d) moveX += speed;
    
    // Apply velocity
    rb.setLinvel({ x: moveX, y: rb.linvel().y, z: moveZ }, true);
    
    // Update camera to follow player
    camera.position.lerp(new THREE.Vector3(pos.x, pos.y + 8, pos.z + 12), 0.05);
    camera.lookAt(pos.x, pos.y + 1, pos.z);
  });
  
  return (
    <RigidBody
      ref={rigidBodyRef}
      position={[position.x, position.y, position.z]}
      enabledRotations={[false, false, false]}
      linearDamping={5}
      type="dynamic"
      colliders={false}
    >
      <CapsuleCollider args={[0.5, 0.4]} position={[0, 0.9, 0]} />
      
      {/* Simple character visual */}
      <group>
        <mesh position={[0, 1, 0]} castShadow>
          <capsuleGeometry args={[0.3, 0.8, 8, 16]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
        <mesh position={[0, 1.7, 0]} castShadow>
          <sphereGeometry args={[0.25, 16, 16]} />
          <meshStandardMaterial color="#DEB887" />
        </mesh>
      </group>
    </RigidBody>
  );
}

// Ground plane
function Ground() {
  return (
    <RigidBody type="fixed" colliders={false}>
      <CuboidCollider args={[50, 0.1, 50]} position={[0, -0.1, 0]} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial color="#4a7c4f" />
      </mesh>
    </RigidBody>
  );
}

// Simple building
function Building({ position, size, color }) {
  return (
    <RigidBody type="fixed" position={position} colliders={false}>
      <CuboidCollider args={[size[0] / 2, size[1] / 2, size[2] / 2]} position={[0, size[1] / 2, 0]} />
      <mesh position={[0, size[1] / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={size} />
        <meshStandardMaterial color={color} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, size[1] + 1.5, 0]} castShadow>
        <coneGeometry args={[Math.max(size[0], size[2]) * 0.7, 3, 4]} />
        <meshStandardMaterial color="#8B0000" />
      </mesh>
    </RigidBody>
  );
}

// Scene content
function SceneContent() {
  const { scene } = useThree();
  const [physicsReady, setPhysicsReady] = useState(false);
  
  // Set scene background
  useEffect(() => {
    scene.background = new THREE.Color('#87CEEB');
    scene.fog = new THREE.Fog('#b8c4ce', 50, 150);
    
    // Give Physics a frame to initialize
    const timeout = setTimeout(() => setPhysicsReady(true), 100);
    
    return () => {
      scene.fog = null;
      clearTimeout(timeout);
    };
  }, [scene]);
  
  if (!physicsReady) {
    return (
      <>
        <ambientLight intensity={0.5} />
        <directionalLight position={[50, 100, 50]} intensity={1} />
      </>
    );
  }
  
  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.5} />
      <directionalLight
        position={[50, 100, 50]}
        intensity={1}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-far={200}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
      />
      
      {/* Ground */}
      <Ground />
      
      {/* Buildings */}
      <Building position={[10, 0, -5]} size={[8, 6, 10]} color="#8B4513" />
      <Building position={[-15, 0, 0]} size={[6, 5, 8]} color="#A0522D" />
      <Building position={[0, 0, -20]} size={[10, 8, 12]} color="#6B4423" />
      
      {/* Player */}
      <Player />
    </>
  );
}

// Error fallback
function ErrorFallback({ error, onReturn }) {
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center p-4">
      <div className="max-w-md text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl text-amber-100 font-bold mb-2">3D World Error</h2>
        <p className="text-amber-100/70 mb-4">
          The 3D environment failed to load.
        </p>
        {error && (
          <p className="text-xs text-red-400 mb-4 font-mono bg-black/50 p-2 rounded">
            {error}
          </p>
        )}
        <button
          onClick={onReturn}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500"
        >
          Return to Map
        </button>
      </div>
    </div>
  );
}

// Loading screen
function LoadingScreen() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-amber-400 text-lg">Loading world...</p>
      </div>
    </div>
  );
}

export default function SimpleGameWorld() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  
  const handleReturn = useCallback(() => {
    navigate('/world/midgard');
  }, [navigate]);
  
  if (error) {
    return <ErrorFallback error={error} onReturn={handleReturn} />;
  }
  
  return (
    <div className="fixed inset-0 bg-black" data-testid="simple-game-world">
      {!loaded && <LoadingScreen />}
      
      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-40 p-4 flex items-center justify-between pointer-events-none">
        <button
          onClick={handleReturn}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all pointer-events-auto"
        >
          <ArrowLeft className="w-5 h-5 text-amber-100" />
          <span className="text-amber-100">Exit</span>
        </button>
        
        <div className="px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <span className="text-amber-100 text-sm">Simple Thornhaven</span>
        </div>
        
        <div className="w-20" />
      </div>
      
      {/* Controls hint */}
      <div className="absolute bottom-4 left-4 z-40 p-3 rounded-lg bg-black/70 border border-amber-900/30 text-sm">
        <p className="text-amber-100 mb-1"><span className="text-amber-400">WASD</span> - Move</p>
        <p className="text-amber-100"><span className="text-amber-400">SHIFT</span> - Run</p>
      </div>
      
      <Canvas
        shadows
        gl={{ antialias: true, alpha: false }}
        onCreated={() => setLoaded(true)}
        onError={(err) => setError(err.message)}
      >
        <Suspense fallback={null}>
          <Physics gravity={[0, -20, 0]} debug={false}>
            <SceneContent />
          </Physics>
        </Suspense>
      </Canvas>
    </div>
  );
}
